<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { fetchReferral } from "@/services/backofficeClient";

const route = useRoute();
const router = useRouter();

const searchTerm = ref("");
const isLoading = ref(false);
const errorMessage = ref("");
const hasSearched = ref(false);
const referral = ref(null);

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const statusBadgeClass = (status) => {
  const normalized = status ? String(status).toLowerCase() : "";
  const base = "badge text-xxs";
  switch (normalized) {
    case "active":
      return `${base} bg-gradient-success text-white`;
    case "deleted":
      return `${base} bg-secondary text-white`;
    case "banned":
      return `${base} bg-gradient-danger text-white`;
    case "not_verified":
    case "notverified":
      return `${base} bg-gradient-warning text-white`;
    case "incomplete_profile":
    case "incompleteprofile":
      return `${base} bg-gradient-warning text-white`;
    case "fraud_suspected":
    case "fraudsuspected":
      return `${base} bg-gradient-danger text-white`;
    default:
      return `${base} bg-light text-dark border`;
  }
};

const parseReferral = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    userId: raw.userId ? String(raw.userId) : "",
    userName: raw.userName || "",
    referralCode: raw.referralCode || "",
    referredBy: raw.referredBy
      ? {
          userId: raw.referredBy.userId ? String(raw.referredBy.userId) : "",
          userName: raw.referredBy.userName || "",
          referredAt: raw.referredBy.referredAt,
        }
      : null,
    referredUsers: Array.isArray(raw.referredUsers)
      ? raw.referredUsers.map((item) => ({
          userId: item.userId ? String(item.userId) : "",
          userName: item.userName || "",
          status: item.status || "",
          referredAt: item.referredAt,
        }))
      : [],
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
};

const submitSearch = async () => {
  const term = searchTerm.value.trim();
  if (!term) {
    errorMessage.value = "Enter a user ID or referral code.";
    return;
  }
  isLoading.value = true;
  errorMessage.value = "";
  hasSearched.value = true;
  referral.value = null;
  try {
    const payload = await fetchReferral(term);
    referral.value = parseReferral(payload);
  } catch (error) {
    console.error("Failed to fetch referral", error);
    referral.value = null;
    errorMessage.value =
      error?.status === 404
        ? "No referral record found for that user ID or code."
        : error?.payload?.message ||
          error?.message ||
          "Failed to load referral. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const goToUser = (userId) => {
  if (!userId) return;
  router.push(`/users/${userId}`);
};

onMounted(() => {
  const prefilled =
    typeof route.query?.search === "string" ? route.query.search.trim() : "";
  if (prefilled) {
    searchTerm.value = prefilled;
    submitSearch();
  }
});
</script>

<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card shadow-sm">
          <div class="card-header pb-0 border-0">
            <h6 class="mb-1">Referral lookup</h6>
            <p class="text-xs text-secondary mb-3">
              Search by user ID or referral code to see who they referred and who referred them.
            </p>
            <form class="row g-2 align-items-center" @submit.prevent="submitSearch">
              <div class="col-12 col-lg-5">
                <div class="input-group input-group-sm">
                  <span class="input-group-text bg-white border-end-0">
                    <i class="ni ni-zoom-split-in"></i>
                  </span>
                  <input
                    type="text"
                    class="form-control border-start-0"
                    placeholder="User ID or referral code"
                    v-model="searchTerm"
                    :disabled="isLoading"
                  />
                  <button
                    class="btn btn-success mb-0"
                    type="submit"
                    :disabled="isLoading"
                  >
                    <i class="fas fa-search"></i>
                  </button>
                </div>
              </div>
            </form>
          </div>

          <div class="card-body px-3 pt-3 pb-4">
            <div v-if="isLoading" class="text-center py-5">
              <div class="spinner-border text-success" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 mb-0 text-sm text-secondary">Loading referral...</p>
            </div>

            <div v-else-if="errorMessage" class="text-center py-5 px-4">
              <div class="icon icon-shape bg-gradient-danger shadow text-center border-radius-md text-white mx-auto">
                <i class="ni ni-fat-remove"></i>
              </div>
              <p class="mt-3 mb-0 text-sm text-dark">{{ errorMessage }}</p>
            </div>

            <div v-else-if="!hasSearched" class="text-center py-5 px-4">
              <p class="text-sm text-secondary mb-0">
                Enter a user ID or referral code to get started.
              </p>
            </div>

            <div v-else-if="referral">
              <div class="row g-3 mb-4">
                <div class="col-12 col-md-4">
                  <p class="text-xs text-secondary mb-0">User</p>
                  <RouterLink
                    v-if="referral.userId"
                    :to="`/users/${referral.userId}`"
                    class="text-sm font-weight-bold text-decoration-none"
                  >
                    {{ referral.userName || "Unknown" }}
                  </RouterLink>
                  <p v-else class="text-sm font-weight-bold mb-0">{{ referral.userName || "Unknown" }}</p>
                  <p class="text-xs text-secondary mb-0">{{ referral.userId }}</p>
                </div>
                <div class="col-12 col-md-4">
                  <p class="text-xs text-secondary mb-0">Referral code</p>
                  <p class="text-sm font-weight-bold mb-0">{{ referral.referralCode || "--" }}</p>
                </div>
                <div class="col-12 col-md-4">
                  <p class="text-xs text-secondary mb-0">Created at</p>
                  <p class="text-sm mb-0">{{ formatDate(referral.createdAt) }}</p>
                </div>
              </div>

              <div class="mb-4">
                <h6 class="text-sm mb-2">Referred by</h6>
                <div v-if="referral.referredBy" class="d-flex align-items-center gap-3 p-3 bg-light border rounded">
                  <div>
                    <RouterLink
                      v-if="referral.referredBy.userId"
                      :to="`/users/${referral.referredBy.userId}`"
                      class="text-sm font-weight-bold text-decoration-none"
                    >
                      {{ referral.referredBy.userName || "Unknown" }}
                    </RouterLink>
                    <p v-else class="text-sm font-weight-bold mb-0">
                      {{ referral.referredBy.userName || "Unknown" }}
                    </p>
                    <p class="text-xs text-secondary mb-0">{{ referral.referredBy.userId }}</p>
                  </div>
                  <span class="text-xs text-secondary ms-auto">
                    Referred at {{ formatDate(referral.referredBy.referredAt) }}
                  </span>
                </div>
                <p v-else class="text-sm text-secondary mb-0">
                  This user was not referred by anyone.
                </p>
              </div>

              <div>
                <h6 class="text-sm mb-2">
                  Referred users
                  <span class="text-secondary">({{ referral.referredUsers.length }})</span>
                </h6>
                <div v-if="referral.referredUsers.length === 0" class="text-sm text-secondary">
                  This user has not referred anyone yet.
                </div>
                <div v-else class="table-responsive">
                  <table class="table align-items-center mb-0">
                    <thead>
                      <tr>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          User
                        </th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Status
                        </th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                          Referred at
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="user in referral.referredUsers"
                        :key="user.userId"
                        @click="goToUser(user.userId)"
                        :style="user.userId ? 'cursor: pointer' : ''"
                      >
                        <td>
                          <div class="d-flex flex-column">
                            <RouterLink
                              v-if="user.userId"
                              :to="`/users/${user.userId}`"
                              class="text-sm font-weight-bold text-reset text-decoration-none"
                              @click.stop
                            >
                              {{ user.userName || "Unknown" }}
                            </RouterLink>
                            <span v-else class="text-sm font-weight-bold">
                              {{ user.userName || "Unknown" }}
                            </span>
                            <span class="text-xs text-secondary">{{ user.userId }}</span>
                          </div>
                        </td>
                        <td>
                          <span :class="statusBadgeClass(user.status)">
                            {{ user.status || "--" }}
                          </span>
                        </td>
                        <td class="text-center">
                          <span class="text-sm">{{ formatDate(user.referredAt) }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
